import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  MapPin, 
  Users, 
  Home,
  Wifi,
  Car,
  Tv,
  Coffee,
  Wind,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X
} from "lucide-react";
import { getPublicLogement, getDisponibilites, createReservation } from "@/lib/api";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";

const equipementIcons = {
  "wifi": Wifi,
  "parking": Car,
  "tv": Tv,
  "cuisine": Coffee,
  "climatisation": Wind,
};

const APARTMENT_IMAGES = [
  "https://images.unsplash.com/photo-1758548157747-285c7012db5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBicmlnaHQlMjBraXRjaGVuJTIwcmVudGFsJTIwYXBhcnRtZW50fGVufDB8fHx8MTc3MzkyODkzNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1760067537740-faa11f7bdf1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjb3p5JTIwYmVkcm9vbSUyMGJvdXRpcXVlJTIwaG90ZWwlMjBzdHlsZXxlbnwwfHx8fDE3NzM5Mjg5Mzd8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1760067537116-de1f76fe8f95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxjb3p5JTIwYmVkcm9vbSUyMGJvdXRpcXVlJTIwaG90ZWwlMjBzdHlsZXxlbnwwfHx8fDE3NzM5Mjg5Mzd8MA&ixlib=rb-4.1.0&q=85"
];

export default function LogementDetailPage() {
  const { id } = useParams();
  const [logement, setLogement] = useState(null);
  const [disponibilites, setDisponibilites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [showReservation, setShowReservation] = useState(false);
  const [selectedArrivee, setSelectedArrivee] = useState(null);
  const [selectedDepart, setSelectedDepart] = useState(null);
  const [reservation, setReservation] = useState({
    voyageur_nom: "",
    voyageur_email: "",
    voyageur_telephone: "",
    date_arrivee: "",
    date_depart: "",
    nb_personnes: 1,
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logementRes, dispoRes] = await Promise.all([
        getPublicLogement(id),
        getDisponibilites(id)
      ]);
      setLogement(logementRes.data);
      setDisponibilites(dispoRes.data);
    } catch (error) {
      console.error("Error loading logement:", error);
      toast.error("Logement non trouvé");
    } finally {
      setLoading(false);
    }
  };

  const getImages = () => {
    if (logement?.photos && logement.photos.length > 0) {
      return logement.photos;
    }
    return APARTMENT_IMAGES;
  };

  const handleSelectDates = (arrivee, depart) => {
    setSelectedArrivee(arrivee);
    setSelectedDepart(depart);
    if (arrivee) {
      setReservation(prev => ({
        ...prev,
        date_arrivee: format(arrivee, "yyyy-MM-dd")
      }));
    }
    if (depart) {
      setReservation(prev => ({
        ...prev,
        date_depart: format(depart, "yyyy-MM-dd")
      }));
    }
  };

  const handleSubmitReservation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createReservation({
        logement_id: id,
        ...reservation
      });
      toast.success("Demande de réservation envoyée ! Nous vous confirmons sous 24h.");
      setShowReservation(false);
      setSelectedArrivee(null);
      setSelectedDepart(null);
      setReservation({
        voyageur_nom: "",
        voyageur_email: "",
        voyageur_telephone: "",
        date_arrivee: "",
        date_depart: "",
        nb_personnes: 1,
        message: ""
      });
    } catch (error) {
      console.error("Error creating reservation:", error);
      toast.error("Erreur lors de l'envoi de la demande");
    } finally {
      setSubmitting(false);
    }
  };

  const calculateNights = () => {
    if (!selectedArrivee || !selectedDepart) return 0;
    return Math.ceil((selectedDepart - selectedArrivee) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#004333]">Chargement...</div>
      </div>
    );
  }

  if (!logement) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-2xl text-[#004333] mb-4">Logement non trouvé</h2>
          <Link to="/voyageurs" className="btn-primary">
            Voir tous les logements
          </Link>
        </div>
      </div>
    );
  }

  const images = getImages();

  return (
    <div className="" data-testid="logement-detail">

      {/* ========== GALLERY ========== */}
      <section className="bg-[#1A120B] pt-4 pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Top bar */}
          <div className="flex items-center justify-between mb-3">
            <Link
              to="/voyageurs"
              className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour aux logements
            </Link>
            {logement.coup_de_coeur && (
              <span className="bg-[#C9922A] text-white px-3 py-1 rounded-full text-xs font-semibold">
                ✦ Coup de cœur
              </span>
            )}
          </div>

          {/* Main image + thumbnails layout */}
          {images.length === 1 ? (
            /* Single image — centered, contained */
            <div
              className="w-full rounded-xl overflow-hidden bg-[#2C1E14] cursor-zoom-in"
              style={{ maxHeight: "520px" }}
              onClick={() => { setCurrentImage(0); setLightboxOpen(true); }}
            >
              <img
                src={images[0]}
                alt={logement.titre}
                className="w-full h-full object-contain"
                style={{ maxHeight: "520px" }}
              />
            </div>
          ) : images.length === 2 ? (
            /* Two images side by side */
            <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden" style={{ height: "460px" }}>
              {images.slice(0, 2).map((src, i) => (
                <div
                  key={i}
                  className="relative overflow-hidden bg-[#2C1E14] cursor-zoom-in group"
                  onClick={() => { setCurrentImage(i); setLightboxOpen(true); }}
                >
                  <img
                    src={src}
                    alt={`${logement.titre} ${i + 1}`}
                    className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                </div>
              ))}
            </div>
          ) : (
            /* 3+ images — large main + right column grid */
            <div className="grid grid-cols-3 gap-2 rounded-xl overflow-hidden" style={{ height: "480px" }}>
              {/* Main image — takes 2 columns */}
              <div
                className="col-span-2 relative overflow-hidden bg-[#2C1E14] cursor-zoom-in group"
                onClick={() => { setCurrentImage(0); setLightboxOpen(true); }}
              >
                <img
                  src={images[0]}
                  alt={logement.titre}
                  className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
              </div>

              {/* Right column — up to 3 thumbnails */}
              <div className="flex flex-col gap-2">
                {images.slice(1, 4).map((src, i) => {
                  const isLast = i === 2 && images.length > 4;
                  return (
                    <div
                      key={i}
                      className="relative flex-1 overflow-hidden bg-[#2C1E14] cursor-zoom-in group"
                      onClick={() => { setCurrentImage(i + 1); setLightboxOpen(true); }}
                    >
                      <img
                        src={src}
                        alt={`${logement.titre} ${i + 2}`}
                        className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                      {isLast && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            +{images.length - 4} photos
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Photo count pill */}
          <div className="flex justify-end mt-2 pb-3">
            <button
              onClick={() => setLightboxOpen(true)}
              className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs transition-colors"
            >
              <span className="w-1 h-1 rounded-full bg-[#C9922A]" />
              Voir les {images.length} photo{images.length > 1 ? "s" : ""}
            </button>
          </div>
        </div>
      </section>

      {/* ========== LIGHTBOX ========== */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Lightbox top bar */}
          <div
            className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            onClick={e => e.stopPropagation()}
          >
            <span className="text-white/60 text-sm">
              {currentImage + 1} / {images.length}
            </span>
            <button
              onClick={() => setLightboxOpen(false)}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Main lightbox image */}
          <div
            className="flex-1 flex items-center justify-center px-16 relative overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setCurrentImage(p => (p - 1 + images.length) % images.length)}
              className="absolute left-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors z-10"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>

            <motion.img
              key={currentImage}
              src={images[currentImage]}
              alt={`${logement.titre} ${currentImage + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg select-none"
              style={{ maxHeight: "calc(100vh - 180px)" }}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              draggable={false}
            />

            <button
              onClick={() => setCurrentImage(p => (p + 1) % images.length)}
              className="absolute right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors z-10"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Thumbnails strip */}
          {images.length > 1 && (
            <div
              className="flex-shrink-0 flex items-center justify-center gap-2 px-6 py-4 overflow-x-auto"
              onClick={e => e.stopPropagation()}
            >
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImage(i)}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    currentImage === i
                      ? "border-[#C9922A] scale-110"
                      : "border-transparent opacity-50 hover:opacity-80"
                  }`}
                >
                  <img
                    src={src}
                    alt={`thumb ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Content */}
      <section className="section-hh bg-[#F5F0EB]">
        <div className="container-hh">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Info */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h1 className="font-display text-3xl md:text-4xl text-[#004333] mb-4">
                  {logement.titre}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 text-[#8B7D6B] mb-8">
                  <span className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#C9922A]" />
                    {logement.adresse}, {logement.ville}
                  </span>
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#C9922A]" />
                    {logement.capacite} personnes
                  </span>
                  <span className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-[#C9922A]" />
                    {logement.type_logement}
                  </span>
                </div>
                
                <div className="bg-[#FFFDF9] p-8 mb-8 rounded-xl border border-[#E0D6CA]">
                  <h2 className="font-display text-xl text-[#004333] mb-4">Description</h2>
                  <p className="text-[#6B5E50] leading-relaxed whitespace-pre-line">
                    {logement.description}
                  </p>
                </div>
                
                {logement.equipements && logement.equipements.length > 0 && (
                  <div className="bg-[#FFFDF9] p-8 mb-8 rounded-xl border border-[#E0D6CA]">
                    <h2 className="font-display text-xl text-[#004333] mb-4">Équipements</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {logement.equipements.map((eq, index) => {
                        const Icon = equipementIcons[eq.toLowerCase()] || Home;
                        return (
                          <div key={index} className="flex items-center gap-3 text-[#6B5E50]">
                            <Icon className="w-5 h-5 text-[#C9922A]" />
                            <span className="capitalize">{eq}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Calendrier de disponibilités interactif */}
                <div className="mb-8">
                  <h2 className="font-display text-xl text-[#004333] mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#C9922A]" />
                    Choisissez vos dates
                  </h2>
                  <AvailabilityCalendar
                    disponibilites={disponibilites}
                    onSelectDates={handleSelectDates}
                    selectedArrivee={selectedArrivee}
                    selectedDepart={selectedDepart}
                  />
                </div>
              </motion.div>
            </div>
            
            {/* Sidebar - Reservation */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                <div className="bg-[#FFFDF9] p-8 shadow-[0_4px_24px_rgba(44,30,20,0.08)] rounded-xl border border-[#E0D6CA]">
                  <div className="mb-6">
                    <span className="font-mono text-3xl font-bold text-[#004333]">
                      {logement.prix_nuit} EUR
                    </span>
                    <span className="text-[#8B7D6B]"> / nuit</span>
                  </div>
                  
                  {/* Résumé dates sélectionnées */}
                  {selectedArrivee && (
                    <div className="mb-6 p-4 bg-[#F5F0EB] border border-[#E0D6CA] rounded-lg">
                      <div className="text-sm space-y-2">
                        <div className="flex justify-between">
                          <span className="text-[#8B7D6B]">Arrivée</span>
                          <span className="font-semibold text-[#004333]">
                            {format(selectedArrivee, "dd/MM/yyyy")}
                          </span>
                        </div>
                        {selectedDepart && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-[#8B7D6B]">Départ</span>
                              <span className="font-semibold text-[#004333]">
                                {format(selectedDepart, "dd/MM/yyyy")}
                              </span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-[#E0D6CA]">
                              <span className="text-[#8B7D6B]">{calculateNights()} nuit(s)</span>
                              <span className="font-mono font-bold text-[#C9922A]">
                                {logement.prix_nuit * calculateNights()} EUR
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => setShowReservation(true)}
                    className="btn-accent w-full mb-4"
                    disabled={!selectedArrivee || !selectedDepart}
                    data-testid="btn-reserver"
                  >
                    {selectedArrivee && selectedDepart 
                      ? "Réserver maintenant" 
                      : "Sélectionnez vos dates"}
                  </button>
                  
                  <p className="text-sm text-[#8B7D6B] text-center">
                    Réponse sous 24h - Sans engagement
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reservation Modal */}
      {showReservation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#FFFDF9] max-w-lg w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl"
          >
            <div className="p-6 border-b border-[#E0D6CA] flex items-center justify-between">
              <h2 className="font-display text-xl text-[#004333]">Demande de réservation</h2>
              <button onClick={() => setShowReservation(false)}>
                <X className="w-6 h-6 text-[#8B7D6B] hover:text-[#004333]" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitReservation} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2D2418] mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  required
                  value={reservation.voyageur_nom}
                  onChange={(e) => setReservation({ ...reservation, voyageur_nom: e.target.value })}
                  className="input-hh"
                  data-testid="reservation-nom"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2D2418] mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={reservation.voyageur_email}
                  onChange={(e) => setReservation({ ...reservation, voyageur_email: e.target.value })}
                  className="input-hh"
                  data-testid="reservation-email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2D2418] mb-2">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  required
                  value={reservation.voyageur_telephone}
                  onChange={(e) => setReservation({ ...reservation, voyageur_telephone: e.target.value })}
                  className="input-hh"
                  data-testid="reservation-telephone"
                />
              </div>
              
              {/* Dates sélectionnées - lecture seule */}
              <div className="p-4 bg-[#004333]/5 border border-[#004333]/20 rounded-lg">
                <h4 className="font-semibold text-[#004333] mb-3">Votre séjour</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#8B7D6B] block">Arrivée</span>
                    <span className="font-semibold text-[#004333]">
                      {selectedArrivee ? format(selectedArrivee, "dd/MM/yyyy") : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#8B7D6B] block">Départ</span>
                    <span className="font-semibold text-[#004333]">
                      {selectedDepart ? format(selectedDepart, "dd/MM/yyyy") : "—"}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2D2418] mb-2">
                  Nombre de personnes
                </label>
                <select
                  value={reservation.nb_personnes}
                  onChange={(e) => setReservation({ ...reservation, nb_personnes: parseInt(e.target.value) })}
                  className="input-hh"
                  data-testid="reservation-personnes"
                >
                  {[...Array(logement.capacite)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1} personne{i > 0 ? "s" : ""}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2D2418] mb-2">
                  Message (optionnel)
                </label>
                <textarea
                  rows={3}
                  value={reservation.message}
                  onChange={(e) => setReservation({ ...reservation, message: e.target.value })}
                  className="input-hh"
                  placeholder="Présentez-vous, motif du séjour..."
                  data-testid="reservation-message"
                />
              </div>
              
              {calculateNights() > 0 && (
                <div className="bg-[#F5F0EB] p-4 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-[#6B5E50]">{logement.prix_nuit} EUR x {calculateNights()} nuits</span>
                    <span className="font-mono font-bold text-[#004333]">
                      {logement.prix_nuit * calculateNights()} €
                    </span>
                  </div>
                  <p className="text-xs text-[#8B7D6B]">
                    *Le montant final sera confirmé par notre équipe
                  </p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={submitting}
                className="btn-accent w-full"
                data-testid="reservation-submit"
              >
                {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
